<template>
  <CorporateOnboardingGuidedCareNearYouContent
    :config="careNearYouConfig"
    :sitter-count="sitterCount"
    :has-fetch-error="hasFetchError"
    :is-loading="isLoading"
    :is-minimum-loading-time="isMinimumLoadingTime"
    @action="handleAction"
  />
</template>

<script lang="ts">
import { PropType } from 'vue';
import { format, subWeeks } from 'date-fns';
import onboardingQueries from '@/graphql/onboarding/queries';
import CorporateOnboardingGuidedCareNearYouContent from './CorporateOnboardingGuidedCareNearYouContent.vue';
import { getCareNearYouConfig } from './careNearYouConfig';
import {
  CORPORATE_ONBOARDING_CARE_NEAR_YOU_ACTION,
  type CorporateOnboardingCareNearYouAction,
} from './types/careNearYouConstants';
import { CorporateOnboardingGuidedDraft } from './types';

export default {
  name: 'CorporateOnboardingGuidedCareNearYou',
  components: { CorporateOnboardingGuidedCareNearYouContent },
  inject: {
    navigateToNextStep: {
      default: () => async () => {},
    },
  },
  props: {
    draft: {
      type: Object as PropType<CorporateOnboardingGuidedDraft>,
      required: true,
    },
  },
  data(): {
    sitterCount: number | null;
    hasFetchError: boolean;
    isLoading: boolean;
    isMinimumLoadingTime: boolean;
    isFirstActivation: boolean;
    minimumLoadingTimerId: ReturnType<typeof setTimeout> | null;
  } {
    return {
      sitterCount: null,
      hasFetchError: false,
      isLoading: true,
      isMinimumLoadingTime: false,
      isFirstActivation: true,
      minimumLoadingTimerId: null,
    };
  },
  computed: {
    careNearYouConfig() {
      return getCareNearYouConfig(this.draft.selection);
    },
  },
  activated() {
    this.clearMinimumLoadingTimer();
    this.isMinimumLoadingTime = false;
    const MIN_LOADING_TIME = 2000;
    this.minimumLoadingTimerId = setTimeout(() => {
      this.isMinimumLoadingTime = true;
      this.minimumLoadingTimerId = null;
    }, MIN_LOADING_TIME);

    if (this.isFirstActivation) {
      this.isFirstActivation = false;
      return;
    }

    if (this.draft.location?.x && this.draft.location?.y) {
      this.isLoading = true;
      this.hasFetchError = false;
      this.$apollo.queries.sitterSearch.refetch();
    }
  },
  deactivated() {
    this.clearMinimumLoadingTimer();
  },
  beforeUnmount() {
    this.clearMinimumLoadingTimer();
  },
  methods: {
    clearMinimumLoadingTimer(): void {
      if (this.minimumLoadingTimerId !== null) {
        clearTimeout(this.minimumLoadingTimerId);
        this.minimumLoadingTimerId = null;
      }
    },
    async handleAction(action: CorporateOnboardingCareNearYouAction): Promise<void> {
      if (action === CORPORATE_ONBOARDING_CARE_NEAR_YOU_ACTION.BROWSE_SITTERS) {
        await this.$router.push({ path: '/availability/search', query: { zipcode: this.draft.zipCode! } });
        return;
      }

      await this.navigateToNextStep({}, { careNearYou: action });
    },
  },
  apollo: {
    sitterSearch: {
      query: onboardingQueries.FETCH_SITTER_COUNT,
      variables(): any {
        return {
          geoPointX: this.draft.location?.x,
          geoPointY: this.draft.location?.y,
          rate: 2000,
          distance: 15,
          lastLoginAt: format(subWeeks(new Date(), 6), 'yyyy-MM-dd'),
        };
      },
      skip(): boolean {
        if (!this.draft.location?.x || !this.draft.location?.y) {
          this.isLoading = false;
          return true;
        }
        return false;
      },
      result({ data, loading }) {
        this.isLoading = loading;
        if (!loading && data?.sitterSearch) {
          this.hasFetchError = false;
          this.sitterCount = data.sitterSearch.totalCount;
        }
      },
      error(error) {
        this.isLoading = false;
        this.hasFetchError = true;
        console.error('Error fetching sitter count:', error);
      },
    },
  },
};
</script>
